import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Users, Building2, CalendarDays, MapPin, ArrowRight } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { PLATFORM_ORDER, platformMeta } from '@/lib/platform';

interface DashboardAuditEntry {
  id: string;
  adminUserId: string | null;
  adminEmail: string | null;
  action: string;
  targetType: string;
  targetId: string | null;
  createdAt: string;
}

interface Dashboard {
  counts: {
    totalUsers: number;
    totalOrgs: number;
    totalActivities: number;
    totalVenues: number;
  };
  last7Days: {
    newUsers: number;
    newActivities: number;
    newSignups: number;
  };
  signupByPlatform: { platform: string; count: number }[];
  seenByPlatform: { platform: string; users: number; activeUsers30d: number }[];
  recentAuditActions: DashboardAuditEntry[];
}

const RU_DAYS = [
  'воскресенье', 'понедельник', 'вторник', 'среда',
  'четверг', 'пятница', 'суббота',
];

const RU_MONTHS_GEN = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

function formatToday(now: Date): string {
  const day = now.getDate();
  const month = RU_MONTHS_GEN[now.getMonth()];
  const year = now.getFullYear();
  const wd = RU_DAYS[now.getDay()];
  return `${day} ${month} ${year}, ${wd}`;
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${dd}.${mm} ${hh}:${mi}`;
  } catch {
    return iso;
  }
}

function shortId(id: string | null): string {
  if (!id) return '—';
  return id.length > 8 ? id.slice(0, 8) : id;
}

interface MetricSpec {
  label: string;
  value: number;
  delta: number | null;
  icon: typeof Users;
  to: string;
  /** rgba for radial-gradient overlay */
  tint: string;
}

function MetricCard({ m }: { m: MetricSpec }) {
  const Icon = m.icon;
  return (
    <Link to={m.to} className="group block">
      <Card
        className="relative overflow-hidden transition-colors hover:bg-zinc-900/60"
        style={{
          backgroundImage: `radial-gradient(circle at top right, ${m.tint}, transparent 55%)`,
        }}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
          <CardTitle className="text-[11px] uppercase tracking-[0.14em] font-medium text-zinc-500">
            {m.label}
          </CardTitle>
          <Icon className="h-3.5 w-3.5 text-zinc-600" />
        </CardHeader>
        <CardContent className="pt-2">
          <div className="font-mono text-[38px] leading-none font-medium tabular-nums tracking-tight text-zinc-50">
            {m.value.toLocaleString('ru')}
          </div>
          <div className="mt-3 flex items-center justify-between text-[11px] text-zinc-500">
            <span>
              {m.delta !== null ? (
                <>
                  <span className="font-mono text-emerald-400 tabular-nums">+{m.delta}</span>
                  <span className="ml-1">за 7 дней</span>
                </>
              ) : (
                <span className="text-zinc-600">всего</span>
              )}
            </span>
            <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity text-zinc-400" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

export function DashboardPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: () =>
      api
        .get<{ data: Dashboard }>('/api/admin/dashboard')
        .then((r) => r.data),
  });

  const todayLabel = formatToday(new Date());

  if (isLoading || !data) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <div>
          <Skeleton className="h-7 w-32" />
          <Skeleton className="h-4 w-48 mt-2" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32 rounded-lg" />
          ))}
        </div>
        <Skeleton className="h-36 rounded-lg" />
        <Skeleton className="h-64 rounded-lg" />
      </div>
    );
  }

  const metrics: MetricSpec[] = [
    {
      label: 'Пользователи',
      value: data.counts.totalUsers,
      delta: data.last7Days.newUsers,
      icon: Users,
      to: '/users',
      tint: 'rgba(59, 130, 246, 0.10)',  // blue
    },
    {
      label: 'Клубы',
      value: data.counts.totalOrgs,
      delta: null,
      icon: Building2,
      to: '/orgs',
      tint: 'rgba(168, 85, 247, 0.10)',  // violet
    },
    {
      label: 'Игры',
      value: data.counts.totalActivities,
      delta: data.last7Days.newActivities,
      icon: CalendarDays,
      to: '/activities',
      tint: 'rgba(255, 92, 53, 0.12)',   // orange
    },
    {
      label: 'Площадки',
      value: data.counts.totalVenues,
      delta: null,
      icon: MapPin,
      to: '/venues',
      tint: 'rgba(234, 179, 8, 0.10)',   // yellow
    },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-[-0.01em] text-zinc-50">Dashboard</h1>
        <div className="mt-1 text-xs text-zinc-500 font-mono tabular-nums">{todayLabel}</div>
      </div>

      {/* Metric grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {metrics.map((m) => <MetricCard key={m.label} m={m} />)}
      </div>

      {/* 7-day activity */}
      <Card>
        <CardHeader>
          <CardTitle>Активность за 7 дней</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-6">
            <ActivityStat label="Новых пользователей" value={data.last7Days.newUsers} />
            <ActivityStat label="Новых игр" value={data.last7Days.newActivities} />
            <ActivityStat label="Записей" value={data.last7Days.newSignups} />
          </div>
        </CardContent>
      </Card>

      {/* Откуда заходят */}
      <SourcesCard
        signupByPlatform={data.signupByPlatform}
        seenByPlatform={data.seenByPlatform}
      />

      {/* Recent audit feed */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Последние действия админа</CardTitle>
          <Link
            to="/audit"
            className="inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-200 transition-colors"
          >
            все <ArrowRight className="h-3 w-3" />
          </Link>
        </CardHeader>
        <CardContent>
          {data.recentAuditActions.length === 0 ? (
            <div className="text-sm text-zinc-500">Пока пусто.</div>
          ) : (
            <ol className="relative space-y-3 pl-5">
              {/* Vertical timeline rail */}
              <div className="absolute left-[5px] top-1.5 bottom-1.5 w-px bg-zinc-800" aria-hidden />
              {data.recentAuditActions.map((a) => (
                <li key={a.id} className="relative flex items-center gap-2 sm:gap-3 flex-wrap">
                  {/* Dot */}
                  <span
                    className="absolute -left-5 top-2 h-2 w-2 rounded-full bg-zinc-700 ring-2 ring-bg-2"
                    aria-hidden
                  />
                  <span className="font-mono text-[11px] tabular-nums text-zinc-500 w-[88px] shrink-0">
                    {formatTimestamp(a.createdAt)}
                  </span>
                  <StatusBadge status={a.action} />
                  <span className="font-mono text-[11px] text-zinc-400 truncate min-w-0">
                    {a.targetType}
                    {a.targetId ? <span className="text-zinc-600">/{shortId(a.targetId)}</span> : null}
                  </span>
                  <span className="sm:ml-auto text-[11px] text-zinc-500 truncate min-w-0">
                    {a.adminEmail ?? '—'}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ActivityStat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 font-medium">
        {label}
      </div>
      <div className="mt-2 font-mono text-3xl tabular-nums font-medium text-zinc-100">
        {value.toLocaleString('ru')}
      </div>
    </div>
  );
}

/**
 * Откуда приходят и откуда заходят, в одной таблице.
 *
 * Колонки «Заходили» и «Активны 30 дней» считают РАЗНЫХ юзеров на платформу, а
 * не события, поэтому их сумма больше числа юзеров: один человек легитимно
 * попадает и в iOS, и в Сайт — это и есть то, что мы хотим видеть.
 */
function SourcesCard({
  signupByPlatform,
  seenByPlatform,
}: {
  signupByPlatform: { platform: string; count: number }[];
  seenByPlatform: { platform: string; users: number; activeUsers30d: number }[];
}) {
  const signup = new Map(signupByPlatform.map((r) => [r.platform, r.count]));
  const seen = new Map(seenByPlatform.map((r) => [r.platform, r]));

  const platforms = PLATFORM_ORDER.filter((p) => signup.has(p) || seen.has(p));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Откуда заходят</CardTitle>
      </CardHeader>
      <CardContent>
        {platforms.length === 0 ? (
          <div className="text-sm text-zinc-500">Данных пока нет.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-[0.14em] text-zinc-500">
                <th className="text-left font-medium pb-2">Платформа</th>
                <th className="text-right font-medium pb-2">Регистраций</th>
                <th className="text-right font-medium pb-2">Заходили</th>
                <th className="text-right font-medium pb-2">Активны 30 дней</th>
              </tr>
            </thead>
            <tbody>
              {platforms.map((p) => {
                const { label, icon: Icon, className } = platformMeta(p);
                const s = seen.get(p);
                return (
                  <tr key={p} className="border-t border-zinc-800">
                    <td className="py-2">
                      <span className={`inline-flex items-center gap-2 ${className}`}>
                        <Icon className="h-3.5 w-3.5" />
                        {label}
                      </span>
                    </td>
                    <Num value={signup.get(p)} />
                    <Num value={s?.users} />
                    <Num value={s?.activeUsers30d} />
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

function Num({ value }: { value: number | undefined }) {
  return (
    <td className="py-2 text-right font-mono tabular-nums text-zinc-100">
      {value === undefined ? <span className="text-zinc-600">—</span> : value.toLocaleString('ru')}
    </td>
  );
}
